import * as React from 'react';
import {MediaPreview, MediaPreviewSubtitle, MediaRemovePreviewButton} from './MediaComponents.style';
import {MobilePreviewComponent} from './MobilePreview';
import {humanFileSize} from '../../../utils';
import {MediaPlayer, mediaSourceUrl, MediaSourceInput} from './MediaPlayer';
import {LessonCard, LessonCardHeader, PosterButton, EmptyLesson} from './MediaPlayer/MediaPlayer.style';

type PreviewFile = Exclude<MediaSourceInput, string | null | undefined> & {size?: number};
type MediaPreviewProps = {
  file: PreviewFile | null | undefined;
  mediaType: 'image' | 'video' | 'audio' | 'attachment';
  removeAll: () => void;
  isUploading: boolean;
  uploadProgress: number;
  customMessage?: string;
  title?: string;
  description?: string;
  alternativeSrc?: string;
  poster?: string;
  lessonCard?: boolean;
};
const ICON = {image: 'images', video: 'play', audio: 'podcast', attachment: 'file-pdf'};
const LABEL = {image: 'Imagem', video: 'Vídeo', audio: 'Áudio', attachment: 'PDF'};
const UX_MESSAGE = {
  image: 'Clique para selecionar ou arraste sua imagem aqui.',
  video: 'Clique para selecionar ou arraste seu vídeo aqui.',
  audio: 'Clique para selecionar ou arraste seu áudio aqui.',
  attachment: 'Clique para selecionar ou arraste seu PDF aqui.'
};

function MediaPreviewImage(props: MediaPreviewProps) {
  return <><MediaPreview><img src={mediaSourceUrl(props.file)} alt={props.title || 'Prévia da imagem'} /></MediaPreview>
    <MobilePreviewComponent file={props.file} mediaType={props.mediaType} title={props.title} description={props.description} />
  </>;
}
function MediaPreviewPdf(props: MediaPreviewProps) {
  return <MediaPreview><object data={mediaSourceUrl(props.file)} type="application/pdf" width="100%" height="100%" aria-label="Prévia do PDF"><a href={mediaSourceUrl(props.file)} target="_blank" rel="noopener noreferrer">Abrir PDF</a></object></MediaPreview>;
}
function MediaPreviewPlayback(props: MediaPreviewProps & {onDuration: (duration: number) => void}) {
  return <MediaPlayer src={mediaSourceUrl(props.file)} mime={props.file?.type || props.file?.mime} alternativeSrc={props.alternativeSrc}
    poster={props.poster} kind={props.mediaType === 'audio' ? 'audio' : 'video'} title={props.title} onDuration={props.onDuration} />;
}

export const MediaPreviewComponent = (props: MediaPreviewProps) => {
  const url = mediaSourceUrl(props.file);
  const [activeSource, setActiveSource] = React.useState('');
  const [metadata, setMetadata] = React.useState({url: '', duration: 0});
  const duration = metadata.url === url ? metadata.duration : 0;
  const active = !props.lessonCard || props.mediaType !== 'video' || activeSource === url;
  const preview = !url ? props.lessonCard ? <EmptyLesson><strong>Adicione {LABEL[props.mediaType].toLowerCase()} à aula</strong><p>{props.customMessage || UX_MESSAGE[props.mediaType]}</p></EmptyLesson>
    : <MediaPreview className="empty-preview"><i className={`fal fa-${ICON[props.mediaType]}`} /><span>{props.customMessage || UX_MESSAGE[props.mediaType]}</span></MediaPreview>
    : props.mediaType === 'image' ? <MediaPreviewImage {...props} />
      : props.mediaType === 'attachment' ? <MediaPreviewPdf {...props} />
        : !active ? <PosterButton type="button" aria-label={`Reproduzir ${props.title || 'vídeo da aula'}`} onClick={event => {event.stopPropagation(); setActiveSource(url);}} onKeyDown={event => event.stopPropagation()}>
          {props.poster && <img src={props.poster} alt="" onError={event => {event.currentTarget.hidden = true;}} />}<span>▶ Reproduzir aula</span>
        </PosterButton> : <MediaPreviewPlayback {...props} onDuration={value => setMetadata({url, duration: value})} />;
  const content = <>{preview}
    {!!props.file?.path && !!props.file.size && <MediaPreviewSubtitle>{props.file.path} - {humanFileSize(props.file.size)}
      <MediaRemovePreviewButton role="button" tabIndex={0} aria-label="Remover arquivo" onClick={event => {event.stopPropagation(); props.removeAll();}} onKeyDown={event => {if (event.key === 'Enter' || event.key === ' ') {event.preventDefault(); event.stopPropagation(); props.removeAll();}}} title="Remover arquivo" />
    </MediaPreviewSubtitle>}
    {props.isUploading && <div className="progress" role="progressbar" aria-valuenow={props.uploadProgress} aria-valuemin={0} aria-valuemax={100}><div className="determinate" style={{width: `${props.uploadProgress}%`}} /></div>}
  </>;
  if (!props.lessonCard) return content;
  return <LessonCard><LessonCardHeader><strong>{props.title || 'Mídia da aula'}</strong><span>{LABEL[props.mediaType]}{duration > 0 ? ` · ${Math.floor(duration / 60)}:${`${Math.floor(duration % 60)}`.padStart(2, '0')}` : ''}</span></LessonCardHeader>{content}</LessonCard>;
};
